/**
 * JukeboxQRCode
 *
 * Renderiza um QR Code apontando para a URL pública do app mobile
 * do Jukebox: `${baseUrl}/jukebox/${telaId}`.
 *
 * Gerado localmente no navegador via `qrcode` — sem depender de API
 * externa. A TV roda 24/7 sem supervisão; um serviço terceiro fora do
 * ar, com rate limit, ou bloqueado pelo Wi-Fi do restaurante deixava
 * o QR (principal CTA de conversão da tela) simplesmente sem renderizar.
 */
import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import * as Sentry from '@sentry/react';

interface Props {
  telaId: string;
  /** URL base do app (ex: https://meusite.com.br ou window.location.origin) */
  baseUrl?: string;
  className?: string;
  /** Modo captura HDMI ativo */
  isCaptura?: boolean;
  /** Há partida de futebol ativa na TV */
  temJogoAtivo?: boolean;
  /** Destaque programático — QR maior, glow e anel pulsante */
  destaque?: boolean;
  /** Substitui o CTA padrão durante o destaque */
  ctaTexto?: string;
}

export default function JukeboxQRCode({
  telaId,
  baseUrl,
  className = '',
  isCaptura = false,
  temJogoAtivo = false,
  destaque = false,
  ctaTexto,
}: Props) {
  const origin     = baseUrl ?? window.location.origin;
  const jukeboxUrl = `${origin}/jukebox/${telaId}`;
  const modoJogo   = isCaptura && temJogoAtivo;

  const accentColor = modoJogo ? '#34d399' : '#fb923c';
  const glowColor   = modoJogo ? 'rgba(52,211,153,0.2)' : 'rgba(249,115,22,0.18)';
  const borderColor = modoJogo ? 'rgba(52,211,153,0.4)' : 'rgba(249,115,22,0.3)';

  const [qrSrc, setQrSrc] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    QRCode.toDataURL(jukeboxUrl, {
      width:  400,
      margin: 2,
      color:  { dark: '#000000', light: '#ffffff' },
    })
      .then(url => { if (!cancelado) setQrSrc(url); })
      .catch(err => {
        console.error('[JukeboxQRCode] Falha ao gerar QR Code:', err);
        Sentry.captureException(err);
      });
    return () => { cancelado = true; };
  }, [jukeboxUrl]);

  const ctaPadrao = modoJogo ? 'Vote · Torça · Peça música' : 'Peça sua música agora';
  const ctaLabel  = destaque && ctaTexto?.trim() ? ctaTexto.trim() : ctaPadrao;

  return (
    <div
      className={`relative flex flex-col items-center rounded-2xl gap-[0.3vw] px-[0.55vw] py-[0.4vw] bg-[linear-gradient(160deg,rgba(20,20,20,0.88)_0%,rgba(0,0,0,0.78)_100%)] backdrop-blur-[16px] transition-[border-color,box-shadow,transform] duration-500 ${destaque ? 'px-[0.75vw] py-[0.55vw] gap-[0.45vw]' : ''} ${className}`}
      style={{
        border:    `1px solid ${destaque ? 'rgba(251,146,60,0.65)' : borderColor}`,
        boxShadow: destaque
          ? `0 0 40px rgba(249,115,22,0.45), 0 0 80px rgba(249,115,22,0.15), inset 0 1px 0 rgba(255,255,255,0.14)`
          : `0 0 22px ${glowColor}, inset 0 1px 0 rgba(255,255,255,0.10), inset 0 -1px 0 rgba(0,0,0,0.15)`,
      }}
    >
      {destaque && (
        <>
          <div
            className="absolute -inset-[0.35vw] rounded-[1.1vw] pointer-events-none animate-pulse-slow"
            style={{ boxShadow: '0 0 0 2px rgba(251,146,60,0.35), 0 0 32px rgba(249,115,22,0.35)' }}
          />
          <div
            className="absolute -inset-[0.7vw] rounded-[1.25vw] pointer-events-none opacity-60"
            style={{ boxShadow: '0 0 0 1px rgba(251,146,60,0.2)' }}
          />
        </>
      )}

      <div
        className={`absolute inset-0 rounded-2xl pointer-events-none transition-[box-shadow] duration-500 ${destaque ? 'animate-pulse' : 'animate-pulse-slow'}`}
        style={{ boxShadow: `0 0 ${destaque ? '36px' : '28px'} ${glowColor}` }}
      />

      <div className="flex items-center justify-center gap-[0.35vw]">
        {modoJogo ? (
          <svg className="w-[0.8vw] h-[0.8vw] shrink-0 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 7l2.5 1.8-1 3.2H10.5l-1-3.2L12 7z" fill="currentColor" stroke="none" />
            <line x1="12" y1="7" x2="12" y2="4.5" /><line x1="14.5" y1="8.8" x2="16.5" y2="7.5" /><line x1="9.5" y1="8.8" x2="7.5" y2="7.5" />
            <line x1="11.5" y1="12" x2="9" y2="14" /><line x1="12.5" y1="12" x2="15" y2="14" />
          </svg>
        ) : (
          <svg className="w-[0.75vw] h-[0.75vw] shrink-0" style={{ color: accentColor }} fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6z" />
          </svg>
        )}
        <p className="text-white font-black uppercase tracking-widest leading-none text-[0.8vw]">
          Jukebox
        </p>
        {modoJogo && (
          <span className="animate-pulse w-[6px] h-[6px] rounded-full bg-emerald-400 inline-block shrink-0 shadow-[0_0_6px_#34d399]" />
        )}
      </div>

      <div className={`w-full h-px ${modoJogo ? 'bg-emerald-500/20' : 'bg-orange-500/25'}`} />

      <div className={`rounded-xl overflow-hidden p-[0.2vw] bg-white ${destaque ? 'w-[8.5vw] h-[8.5vw]' : 'w-[7vw] h-[7vw]'}`}>
        {qrSrc && (
          <img
            src={qrSrc}
            alt={`QR Code Jukebox — ${jukeboxUrl}`}
            className="block w-full h-full"
            style={{ imageRendering: 'crisp-edges' }}
          />
        )}
      </div>

      <p
        className={`font-bold uppercase tracking-wider leading-none text-center whitespace-nowrap text-[0.55vw] ${destaque ? 'animate-pulse text-[0.62vw]' : 'animate-pulse-slow'}`}
        style={{ color: accentColor }}
      >
        {ctaLabel}
      </p>
    </div>
  );
}
